import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import type {AuthToken} from '@github-ui/copilot-auth-token/auth-token'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {readSandboxImageOverride} from '@github-ui/copilot-shared-ui/utils/sandbox-image-override'
import {tasksApiPath} from '../utils/api-url-helpers'

const ON_DEMAND_SANDBOX_ENVIRONMENT_ID = 'github-sandbox'

interface EnvironmentTaskResponse {
  id?: string
  task?: {id?: string}
  repository_clone_url?: string
  reserved?: boolean
  reservation_expires_at?: string
  claimed?: boolean
  sessions?: Array<{id?: string; environment_id?: string}>
}

interface EnvironmentBoundTaskInput {
  environmentId: string
  problemStatement: string
  copilotApiUrl?: string
  authToken?: AuthToken
  model?: string
  reasoningEffort?: ReasoningEffort
  space?: {owner: string; slug: string}
  /** Asks Mission Control to hide the task from lists until a prompt claims it. */
  reserve?: boolean
  /**
   * A reservation held by this tab. With `reserve`, Mission Control renews it or swaps it for a new reservation.
   * Without `reserve`, Mission Control claims it for this prompt or falls back to a normal create.
   */
  reservedTaskId?: string
  origin?: {
    type: string
    identifiers: readonly string[]
    url: string
  }
}

interface RepositoryEnvironmentBoundTaskInput extends EnvironmentBoundTaskInput {
  repositoryNwo: string
  baseRef?: string
  computeScope?: never
}

interface RepositorylessEnvironmentBoundTaskInput extends EnvironmentBoundTaskInput {
  repositoryNwo?: undefined
  baseRef?: never
  computeScope?: string
}

export type CreateEnvironmentBoundTaskInput =
  | RepositoryEnvironmentBoundTaskInput
  | RepositorylessEnvironmentBoundTaskInput

export interface EnvironmentBoundTask {
  taskId: string
  sessionId: string
  boundEnvironmentId?: string
  repositoryCloneUrl?: string
  /** Present when Mission Control accepted a `reserve` request. */
  reservation?: {expiresAt: number}
  /** True when Mission Control claimed the reserved task named by `reservedTaskId` for this prompt. */
  claimed?: boolean
}

export async function createEnvironmentBoundTask(
  input: CreateEnvironmentBoundTaskInput,
  onTaskAllocated?: (taskId: string, allocation: {reserved: boolean}) => void,
): Promise<EnvironmentBoundTask> {
  const repository = parseNwo(input.repositoryNwo)
  const normalizedInput = normalizeEnvironmentBoundTaskInput(input)
  const image =
    normalizedInput.reserve || normalizedInput.environmentId !== ON_DEMAND_SANDBOX_ENVIRONMENT_ID
      ? undefined
      : readSandboxImageOverride()
  const reservedTaskId = image ? undefined : normalizedInput.reservedTaskId
  const computeScope = repository ? undefined : normalizedInput.computeScope
  const response = await fetchCAPI({
    path: tasksApiPath(),
    copilotApiUrl: input.copilotApiUrl,
    authToken: input.authToken,
    method: 'POST',
    additionalHeaders: {Accept: 'application/json'},
    body: {
      environment_id: normalizedInput.environmentId,
      prompt: normalizedInput.problemStatement,
      ...(normalizedInput.model ? {model: normalizedInput.model} : {}),
      ...(normalizedInput.reasoningEffort ? {reasoning_effort: normalizedInput.reasoningEffort} : {}),
      ...(repository ? {repositories: [repository]} : {}),
      ...(repository && normalizedInput.baseRef ? {base_ref: normalizedInput.baseRef} : {}),
      ...(computeScope || image
        ? {compute: {...(computeScope ? {scope: computeScope} : {}), ...(image ? {image} : {})}}
        : {}),
      ...(normalizedInput.environmentId === ON_DEMAND_SANDBOX_ENVIRONMENT_ID && normalizedInput.space
        ? {space: normalizedInput.space}
        : {}),
      ...(normalizedInput.reserve ? {reserve: true} : {}),
      ...(reservedTaskId ? {reserved_task_id: reservedTaskId} : {}),
      ...(normalizedInput.origin
        ? {
            event_type: normalizedInput.origin.type,
            event_identifiers: [
              ...normalizedInput.origin.identifiers,
              `${normalizedInput.origin.type}_url::${normalizedInput.origin.url}`,
            ],
          }
        : {}),
    },
  })
  const body = (await response.json()) as EnvironmentTaskResponse
  const taskId = body.id ?? body.task?.id
  if (!taskId) throw new Error('Mission Control did not return a task id for this environment task')
  const reservation = normalizedInput.reserve ? parseReservation(body) : undefined
  onTaskAllocated?.(taskId, {reserved: reservation !== undefined})

  const session = body.sessions?.[0]
  if (!session?.id) throw new Error('Mission Control did not create a session for this environment')

  const boundEnvironmentId = session.environment_id
  if (normalizedInput.environmentId === ON_DEMAND_SANDBOX_ENVIRONMENT_ID && !boundEnvironmentId) {
    throw new Error('Mission Control did not bind the cloud session to an environment')
  }

  return {
    taskId,
    sessionId: session.id,
    boundEnvironmentId,
    ...(body.repository_clone_url ? {repositoryCloneUrl: body.repository_clone_url} : {}),
    ...(reservation ? {reservation} : {}),
    // Reserve and renew responses also carry `claimed: false`; only a submit naming a reservation can claim it.
    ...(!normalizedInput.reserve && reservedTaskId && body.claimed === true ? {claimed: true} : {}),
  }
}

function parseReservation(body: EnvironmentTaskResponse): {expiresAt: number} | undefined {
  if (body.reserved !== true || !body.reservation_expires_at) return undefined
  const expiresAt = Date.parse(body.reservation_expires_at)
  return Number.isFinite(expiresAt) ? {expiresAt} : undefined
}

function normalizeEnvironmentBoundTaskInput({
  environmentId,
  problemStatement,
  model,
  reasoningEffort,
  repositoryNwo,
  baseRef,
  computeScope,
  space,
  reserve,
  reservedTaskId,
  origin,
}: CreateEnvironmentBoundTaskInput): CreateEnvironmentBoundTaskInput {
  if (repositoryNwo) {
    return {
      environmentId,
      problemStatement,
      ...(model ? {model} : {}),
      ...(reasoningEffort ? {reasoningEffort} : {}),
      repositoryNwo,
      ...(baseRef ? {baseRef} : {}),
      ...(space ? {space} : {}),
      ...(reserve ? {reserve} : {}),
      ...(reservedTaskId ? {reservedTaskId} : {}),
      ...(origin ? {origin} : {}),
    }
  }

  return {
    environmentId,
    problemStatement,
    ...(model ? {model} : {}),
    ...(reasoningEffort ? {reasoningEffort} : {}),
    ...(computeScope ? {computeScope} : {}),
    ...(space ? {space} : {}),
    ...(reserve ? {reserve} : {}),
    ...(reservedTaskId ? {reservedTaskId} : {}),
    ...(origin ? {origin} : {}),
  }
}

function parseNwo(nwo: string | undefined): {owner: string; name: string} | undefined {
  if (nwo === undefined) return undefined
  const [owner, name, ...extraSegments] = nwo.split('/')
  if (!owner || !name || extraSegments.length > 0) {
    throw new Error('Repository must use the owner/name format.')
  }
  return {owner, name}
}
